export interface WaterfallScaleRange {
    readonly maxDb: number;
    readonly minDb: number;
}

const WATERFALL_HISTOGRAM_MIN_DB = -130;
const WATERFALL_HISTOGRAM_MAX_DB = 10;
const WATERFALL_HISTOGRAM_BUCKETS = 280;
const WATERFALL_MIN_DYNAMIC_RANGE_DB = 18;
const WATERFALL_MAX_DYNAMIC_RANGE_DB = 70;
const WATERFALL_SCALE_UPDATE_INTERVAL_MS = 220;
const WATERFALL_SCALE_DEADBAND_DB = 1.2;
const WATERFALL_SCALE_ALPHA_MIN_RISE = 0.09;
const WATERFALL_SCALE_ALPHA_MIN_FALL = 0.2;
const WATERFALL_SCALE_ALPHA_MAX_RISE = 0.3;
const WATERFALL_SCALE_ALPHA_MAX_FALL = 0.08;
const WATERFALL_DEFAULT_SCALE: WaterfallScaleRange = {
    maxDb: -35,
    minDb: -95,
};

const WATERFALL_COLOR_STOPS: readonly { b: number; g: number; r: number; t: number }[] = [
    { b: 29, g: 23, r: 20, t: 0 },
    { b: 64, g: 52, r: 42, t: 0.48 },
    { b: 116, g: 100, r: 84, t: 0.8 },
    { b: 84, g: 146, r: 188, t: 0.92 },
    { b: 57, g: 92, r: 214, t: 1 },
];

function clampNumber(value: number, minValue: number, maxValue: number) {
    return Math.min(maxValue, Math.max(minValue, value));
}

function getHistogramQuantile(params: {
    bucketCount: number;
    counts: Uint32Array;
    maxDb: number;
    minDb: number;
    quantile: number;
    totalCount: number;
}) {
    const target = params.totalCount * clampNumber(params.quantile, 0, 1);
    let cumulativeCount = 0;

    for (let bucketIndex = 0; bucketIndex < params.bucketCount; bucketIndex += 1) {
        cumulativeCount += params.counts[bucketIndex] ?? 0;

        if (cumulativeCount >= target) {
            const ratio = bucketIndex / Math.max(1, params.bucketCount - 1);
            return params.minDb + ratio * (params.maxDb - params.minDb);
        }
    }

    return params.maxDb;
}

export function resolveAdaptiveWaterfallScale(params: {
    colorOffsetDb: number;
    columnStep: number;
    maxBinIndex: number;
    rows: readonly (readonly number[])[];
}) {
    const histogramCounts = new Uint32Array(WATERFALL_HISTOGRAM_BUCKETS);
    const histogramRange = WATERFALL_HISTOGRAM_MAX_DB - WATERFALL_HISTOGRAM_MIN_DB;
    let sampleCount = 0;

    for (const row of params.rows) {
        const rowLength = row.length;

        if (rowLength === 0) {
            continue;
        }

        const rowMaxBinIndex = Math.min(params.maxBinIndex, rowLength - 1);

        if (rowMaxBinIndex < 0) {
            continue;
        }

        for (let binIndex = 0; binIndex <= rowMaxBinIndex; binIndex += params.columnStep) {
            const rawValue = row[binIndex];

            if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) {
                continue;
            }

            const adjustedValue = rawValue + params.colorOffsetDb;
            const clampedValue = clampNumber(adjustedValue, WATERFALL_HISTOGRAM_MIN_DB, WATERFALL_HISTOGRAM_MAX_DB);
            const ratio = (clampedValue - WATERFALL_HISTOGRAM_MIN_DB) / Math.max(1e-6, histogramRange);
            const bucketIndex = clampNumber(
                Math.floor(ratio * (WATERFALL_HISTOGRAM_BUCKETS - 1)),
                0,
                WATERFALL_HISTOGRAM_BUCKETS - 1,
            );

            histogramCounts[bucketIndex] = (histogramCounts[bucketIndex] ?? 0) + 1;
            sampleCount += 1;
        }
    }

    if (sampleCount < 32) {
        return WATERFALL_DEFAULT_SCALE;
    }

    const noiseFloorDb = getHistogramQuantile({
        bucketCount: WATERFALL_HISTOGRAM_BUCKETS,
        counts: histogramCounts,
        maxDb: WATERFALL_HISTOGRAM_MAX_DB,
        minDb: WATERFALL_HISTOGRAM_MIN_DB,
        quantile: 0.3,
        totalCount: sampleCount,
    });
    const peakDb = getHistogramQuantile({
        bucketCount: WATERFALL_HISTOGRAM_BUCKETS,
        counts: histogramCounts,
        maxDb: WATERFALL_HISTOGRAM_MAX_DB,
        minDb: WATERFALL_HISTOGRAM_MIN_DB,
        quantile: 0.99,
        totalCount: sampleCount,
    });

    let nextMinDb = clampNumber(noiseFloorDb - 4, WATERFALL_HISTOGRAM_MIN_DB, WATERFALL_HISTOGRAM_MAX_DB);
    let nextMaxDb = clampNumber(peakDb + 1.5, WATERFALL_HISTOGRAM_MIN_DB, WATERFALL_HISTOGRAM_MAX_DB);

    if (nextMaxDb - nextMinDb < WATERFALL_MIN_DYNAMIC_RANGE_DB) {
        nextMaxDb = nextMinDb + WATERFALL_MIN_DYNAMIC_RANGE_DB;
    }

    if (nextMaxDb - nextMinDb > WATERFALL_MAX_DYNAMIC_RANGE_DB) {
        nextMinDb = nextMaxDb - WATERFALL_MAX_DYNAMIC_RANGE_DB;
    }

    nextMinDb = clampNumber(nextMinDb, WATERFALL_HISTOGRAM_MIN_DB, WATERFALL_HISTOGRAM_MAX_DB - 1);
    nextMaxDb = clampNumber(nextMaxDb, nextMinDb + 1, WATERFALL_HISTOGRAM_MAX_DB);

    return {
        maxDb: nextMaxDb,
        minDb: nextMinDb,
    } satisfies WaterfallScaleRange;
}

function quantizeDb(valueDb: number) {
    return Math.round(valueDb * 2) / 2;
}

export function smoothAdaptiveWaterfallScale(params: {
    lastUpdatedAtMs: number;
    nowMs: number;
    previousScale: WaterfallScaleRange | null;
    rawScale: WaterfallScaleRange;
}) {
    const { lastUpdatedAtMs, nowMs, previousScale, rawScale } = params;

    if (!previousScale) {
        return {
            didUpdate: true,
            scale: {
                maxDb: quantizeDb(rawScale.maxDb),
                minDb: quantizeDb(rawScale.minDb),
            } satisfies WaterfallScaleRange,
        };
    }

    if (nowMs - lastUpdatedAtMs < WATERFALL_SCALE_UPDATE_INTERVAL_MS) {
        return {
            didUpdate: false,
            scale: previousScale,
        };
    }

    const minDelta = rawScale.minDb - previousScale.minDb;
    const maxDelta = rawScale.maxDb - previousScale.maxDb;

    if (Math.abs(minDelta) < WATERFALL_SCALE_DEADBAND_DB && Math.abs(maxDelta) < WATERFALL_SCALE_DEADBAND_DB) {
        return {
            didUpdate: false,
            scale: previousScale,
        };
    }

    const minAlpha = minDelta < 0 ? WATERFALL_SCALE_ALPHA_MIN_FALL : WATERFALL_SCALE_ALPHA_MIN_RISE;
    const maxAlpha = maxDelta > 0 ? WATERFALL_SCALE_ALPHA_MAX_RISE : WATERFALL_SCALE_ALPHA_MAX_FALL;

    let nextMinDb = previousScale.minDb + minDelta * minAlpha;
    let nextMaxDb = previousScale.maxDb + maxDelta * maxAlpha;

    if (nextMaxDb - nextMinDb < WATERFALL_MIN_DYNAMIC_RANGE_DB) {
        nextMaxDb = nextMinDb + WATERFALL_MIN_DYNAMIC_RANGE_DB;
    }

    if (nextMaxDb - nextMinDb > WATERFALL_MAX_DYNAMIC_RANGE_DB) {
        nextMinDb = nextMaxDb - WATERFALL_MAX_DYNAMIC_RANGE_DB;
    }

    nextMinDb = clampNumber(nextMinDb, WATERFALL_HISTOGRAM_MIN_DB, WATERFALL_HISTOGRAM_MAX_DB - 1);
    nextMaxDb = clampNumber(nextMaxDb, nextMinDb + 1, WATERFALL_HISTOGRAM_MAX_DB);

    return {
        didUpdate: true,
        scale: {
            maxDb: quantizeDb(nextMaxDb),
            minDb: quantizeDb(nextMinDb),
        } satisfies WaterfallScaleRange,
    };
}

export function getAdaptiveWaterfallColor(valueDb: number, scale: WaterfallScaleRange) {
    const normalizedValue = clampNumber((valueDb - scale.minDb) / Math.max(1e-6, scale.maxDb - scale.minDb), 0, 1);

    let fromStop = WATERFALL_COLOR_STOPS[0]!;
    let toStop = WATERFALL_COLOR_STOPS.at(-1)!;

    for (let stopIndex = 1; stopIndex < WATERFALL_COLOR_STOPS.length; stopIndex += 1) {
        const candidateStop = WATERFALL_COLOR_STOPS[stopIndex]!;

        if (normalizedValue <= candidateStop.t) {
            toStop = candidateStop;
            fromStop = WATERFALL_COLOR_STOPS[stopIndex - 1]!;
            break;
        }
    }

    const segmentRatio = clampNumber((normalizedValue - fromStop.t) / Math.max(1e-6, toStop.t - fromStop.t), 0, 1);
    const redChannel = Math.round(fromStop.r + (toStop.r - fromStop.r) * segmentRatio);
    const greenChannel = Math.round(fromStop.g + (toStop.g - fromStop.g) * segmentRatio);
    const blueChannel = Math.round(fromStop.b + (toStop.b - fromStop.b) * segmentRatio);

    return `rgb(${redChannel.toString()},${greenChannel.toString()},${blueChannel.toString()})`;
}
